import { useField } from "formik";
import { useEffect, useState } from "react";
import { LoadingScreen } from "@/components/ui/LoadingScreen";
import SelectField from "@/components/form/SelectField";
import { useUsers } from "@/hooks/useCommonQueries";

interface User {
  id: number;
  first_name: string;
  last_name: string;
}

interface SelectUserFieldProps {
  name: string;
  label: string;
  description?: string;
  required?: boolean;
  multiple?: boolean;
  className?: string;
  disabled?: boolean;
  isEditing?: boolean;
  individualEditAllowed?: boolean;
  onChange?: (value: number | number[]) => void;
  onEditSaved?: (name: string, value: number | number[]) => void;
  onIndividualEditModeChanged?: (isIndividualEditing: boolean) => void;
}

export default function SelectUserField({
  name,
  label,
  description,
  required = false,
  multiple = false,
  className,
  disabled = false,
  isEditing = true,
  individualEditAllowed = false,
  onChange,
  onEditSaved,
  onIndividualEditModeChanged,
}: SelectUserFieldProps) {
  const [field, , helpers] = useField<
    number | number[] | User | User[] | undefined
  >(name);
  const { data: users, isLoading: isLoadingUsers } = useUsers();
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Normalize field value on initial load and value changes
  useEffect(() => {
    let mounted = true;

    const normalizeValue = () => {
      if (!field.value) {
        if (mounted) {
          helpers.setValue(multiple ? [] : undefined);
        }
        return;
      }

      let normalizedValue: number | number[];

      if (Array.isArray(field.value)) {
        if (field.value.length === 0) {
          normalizedValue = [];
        } else if (
          typeof field.value[0] === "object" &&
          "id" in field.value[0]
        ) {
          // Array of User objects
          normalizedValue = (field.value as User[]).map((user) => user.id);
        } else {
          // Array of IDs
          normalizedValue = field.value as number[];
        }
        if (!multiple && Array.isArray(normalizedValue)) {
          normalizedValue = normalizedValue[0];
        }
      } else if (typeof field.value === "object" && "id" in field.value) {
        // Single User object
        normalizedValue = (field.value as User).id;
        if (multiple) {
          normalizedValue = [normalizedValue];
        }
      } else {
        // Single ID
        normalizedValue = field.value as number;
        if (multiple) {
          normalizedValue = [normalizedValue];
        }
      }

      if (mounted) {
        helpers.setValue(normalizedValue);
        if (onChange) {
          onChange(normalizedValue);
        }
      }
    };

    if (isInitialLoad) {
      normalizeValue();
      setIsInitialLoad(false);
    }

    return () => {
      mounted = false;
    };
  }, [field.value, helpers, multiple, isInitialLoad, onChange]);

  if (isLoadingUsers) {
    return <LoadingScreen fullPage={false} />;
  }

  return (
    <SelectField
      name={name}
      label={label}
      description={description}
      required={required}
      options={{ multiple }}
      className={className}
      disabled={disabled}
      type="number"
      isEditing={isEditing}
      individualEditAllowed={individualEditAllowed}
      onEditSaved={(name, value) => {
        onEditSaved?.(name, value as number | number[]);
      }}
      onIndividualEditModeChanged={onIndividualEditModeChanged}
    >
      {users
        ?.sort((a, b) => a.first_name.localeCompare(b.first_name))
        .map((user) => (
          <SelectField.Item key={user.id} value={user.id}>
            {user.first_name} {user.last_name} ({user.email})
          </SelectField.Item>
        ))}
    </SelectField>
  );
}
